import trainingStyles from "../styles/training.css?url";
import { useContext } from "react";
import { PublishedShellContext } from "./published-viewer";
import { getRouteApi, notFound, redirect } from "@tanstack/react-router";
import { nativeFeatureQuery } from "./links";
import { loadDocumentPage, type DocumentPageData, type MissingPage } from "../server/page";
import type { SiteShell } from "../server/shell";
import { creatorPageHead, shellFrom, withStylesheet } from "./head";
import { walkPageNodes } from "../../../shared/src/page-tree";
import { readyPageNodes } from "./page-readiness";
import type { PageDocument } from "../../../shared/src/pages";
import updatesControls from "../styles/updates-controls.css?url";
import bibleStyles from "../styles/bible.css?url";
import interactionStyles from "../styles/page-interactions.css?url";
import formStyles from "../styles/page-forms.css?url";

const root = getRouteApi("__root__");

/** The site's shell for the page on screen. */
export function useShell() {
  const loaded = root.useLoaderData() as SiteShell;
  return useContext(PublishedShellContext) ?? loaded;
}

/**
 * A route's authored page. A redirect the creator set up is followed; a route with a native page
 * of its own shows it when nothing is composed; any other missing page is a 404 inside the site's
 * layout.
 */
export async function loadPage(path: string, missing: MissingPage) {
  const data = await loadDocumentPage({ data: { path, missing } });
  if (data.kind === "redirect") throw redirect({ href: data.to, statusCode: 302 });
  if (data.kind === "missing") throw notFound();
  // Opened in the browser, a page waits for its League elements' code rather than drawing late.
  // The server readied them with the page (server/document.server.ts).
  if (!import.meta.env.SSR && data.kind === "page" && data.document && data.bindings)
    await readyPageNodes(data.document, data.bindings.components);
  return data;
}

/**
 * A feature's index page (News, Library, the League Bible, Membership...): the page the creator
 * composed for it, or the feature's native page. Searches, filters and checkouts always show the
 * native page.
 */
export async function loadFeaturePage(path: string, location: { searchStr: string }) {
  if (nativeFeatureQuery(path, new URLSearchParams(location.searchStr)))
    return { kind: "native" as const };
  return loadPage(path, "native");
}

export type LoadedPage =
  | Exclude<DocumentPageData, { kind: "missing" | "redirect" }>
  | {
      kind: "native";
    };

/** An authored page's head with the stylesheets its elements need beyond the site's own. */
export function withDocumentStyles<Head extends Parameters<typeof withStylesheet>[0]>(
  head: Head,
  document: Pick<PageDocument, "nodes"> | null | undefined,
) {
  const nodes = document ? walkPageNodes(document) : [];
  const themed = nodes.some((node) => node.kind === "builtIn" && node.page === "training")
    ? withStylesheet(head, trainingStyles)
    : head;
  const controlled = nodes.some((node) => node.kind === "updates" && node.ownerControls)
    ? withStylesheet(themed, updatesControls)
    : themed;
  // A component's definition is bound separately; an instance may contain either interaction.
  const forms = nodes.some((node) => node.kind === "collection" && node.search)
    ? withStylesheet(controlled, formStyles)
    : controlled;
  const interactive = nodes.some(
    (node) => node.kind === "tabs" || node.kind === "accordion" || node.kind === "component",
  )
    ? withStylesheet(forms, interactionStyles)
    : forms;
  // League cards and game icons in text take the Handbook's styles.
  return nodes.some(
    (node) =>
      node.kind === "leagueHandbook" ||
      node.kind === "leagueSection" ||
      node.kind === "leagueBlock" ||
      (node.kind === "text" && node.rich),
  )
    ? withStylesheet(interactive, bibleStyles)
    : interactive;
}

/** The head for an authored page, or the route's own when it shows a native or missing page. */
export function documentHead(
  matches: readonly { loaderData?: unknown; routeId: string }[],
  data: LoadedPage | undefined,
  pathname: string,
  native: { title: (shell: SiteShell) => string; description: (shell: SiteShell) => string },
) {
  const shell = shellFrom(matches);
  if (!shell) return {};
  if (data?.kind === "page") {
    const head = creatorPageHead(shell, {
      pathname,
      title: data.page.seoTitle || data.page.title,
      description: data.page.description,
      canonicalPath: data.page.path,
      noindex: data.private || data.locked || data.page.hideFromSearch,
      preloadImage: data.lead,
    });
    return withDocumentStyles(head, data.document);
  }
  return creatorPageHead(shell, {
    pathname,
    title: native.title(shell),
    description: native.description(shell),
    homepageSeo: pathname === "/",
  });
}
